import React from "react";
import { Message } from "primereact/message";
import { FunnelStage } from "../../../../types/onboardingAnalytics";
import { OnboardingState } from "../../../../types/onboarding";
import { stateLabel } from "../../../../utils/onboardingStates";
import {
  firstFunnelViolation,
  formatCount,
  formatPercent,
} from "../../../../utils/analyticsFormat";

interface FunnelChartProps {
  stages: FunnelStage[];
}

/**
 * The pipeline funnel: how many prospects reached each milestone, and how many
 * were lost on the way to the next.
 *
 * **Why this refuses to draw sometimes.** Stage counts are cumulative — each
 * counts prospects at or past that stage — so in pipeline order they can only
 * decrease. If a count rises, the server has ordered its stages wrongly, which
 * makes one drop-off negative and the one before it inflated. A bar chart drawn
 * from that looks entirely plausible and is wrong, and a plausible wrong answer
 * is worse than no answer, so the section says what is inconsistent instead
 * (FR-019, research R3).
 *
 * At the time of writing the deployed backend does exactly this: it returns
 * seven stages with STATION_PUBLIC placed fourth, before OCPP_CREDENTIALS_SENT.
 * The guard is not a workaround for that one bug though — stage order is the
 * server's to choose, and any future reordering would fail the same way
 * silently.
 *
 * Bars are drawn as plain elements rather than through a chart library: the
 * funnel is a list of labelled proportions, and rendering it as markup keeps
 * every label readable by a test and by a screen reader.
 */
const FunnelChart: React.FC<FunnelChartProps> = ({ stages }) => {
  const violation = firstFunnelViolation(stages);

  if (violation) {
    return (
      <Message
        severity="warn"
        className="w-full"
        content={
          <div className="line-height-3">
            <div className="font-semibold mb-1">
              This funnel can't be drawn — the data is inconsistent.
            </div>
            <div>
              More prospects are counted at{" "}
              <strong>
                {stateLabel(violation.current.state as OnboardingState)}
              </strong>{" "}
              ({formatCount(violation.current.count)}) than at the earlier stage{" "}
              <strong>
                {stateLabel(violation.previous.state as OnboardingState)}
              </strong>{" "}
              ({formatCount(violation.previous.count)}). Because these counts are
              cumulative, that cannot happen — the stages have come back out of
              order, so the drop-off figures would be wrong.
            </div>
          </div>
        }
      />
    );
  }

  if (!stages.length) return null;

  const widest = Math.max(...stages.map((s) => s.count), 1);

  return (
    <div className="flex flex-column gap-3" data-testid="funnel">
      {stages.map((stage) => {
        const pct = formatPercent(stage.dropOffPct);
        const width = Math.max((stage.count / widest) * 100, 1);
        return (
          <div key={stage.state}>
            <div className="flex justify-content-between align-items-baseline mb-1 gap-2">
              <span className="font-medium text-900">
                {stateLabel(stage.state as OnboardingState)}
              </span>
              <span className="text-900 font-semibold">
                {formatCount(stage.count)}
              </span>
            </div>
            <div
              className="border-round overflow-hidden"
              style={{ background: "var(--surface-200)", height: "0.75rem" }}
            >
              <div
                style={{
                  width: `${width}%`,
                  height: "100%",
                  background: "var(--primary-color)",
                }}
              />
            </div>
            {stage.dropOff != null && (
              <div className="text-sm text-500 mt-1">
                {formatCount(stage.dropOff)} lost since the previous stage
                {/* Nothing at all where the API sent null: rendering 0% would
                    claim nobody was lost, which is a different statement. */}
                {pct && ` (${pct})`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FunnelChart;
